import * as React from "react"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import { AXIOS_API_INSTANCE } from "../../../Constants"
import { send_authorized_request } from "../../../Constants/cognito"
import { useSnackbar } from 'notistack';
import { Box, FormControl, IconButton, Tooltip, Checkbox, FormControlLabel, InputLabel, Select, MenuItem, Accordion, AccordionSummary, Typography, AccordionDetails, InputAdornment, Divider } from "@mui/material"
import Grid from "@mui/material/Grid2"
import { LoadingBackdropIndeterminate } from "../../LoadingBackdrop"
import { moneyFormat, parseMoneyInput } from "../../../Utils"
import dayjs from "dayjs"

const statuses = ['Created', 'Filed', 'Fulfilled']

const findTotalDiscount = (newWarrantiedServices, newWarrantiedParts, newWarrantiedCharges, oldWarrantiedServices, oldWarrantiedParts, oldWarrantiedCharges) => {
    var total = 0
    Object.keys(newWarrantiedServices).forEach((x) => {
        if (newWarrantiedServices[x].selected) {
            total += newWarrantiedServices[x].estimated_coverage
        }
    })
    Object.keys(newWarrantiedCharges).forEach((x) => {
        if (newWarrantiedCharges[x].selected) {
            total += newWarrantiedCharges[x].estimated_coverage
        }
    })
    Object.keys(oldWarrantiedServices).forEach((x) => {
        total += oldWarrantiedServices[x].estimated_coverage
    })
    Object.keys(oldWarrantiedParts).forEach((x) => {
        total += oldWarrantiedParts[x].estimated_coverage
    })
    Object.keys(oldWarrantiedCharges).forEach((x) => {
        total += oldWarrantiedCharges[x].estimated_coverage
    })
    return total
}

export default function ServiceOrderWarrantyModal({
    serviceOrderWarranty,
    open,
    closeModal,
    creatingNew,
    services,
    charges,
    serviceOrderId
}) {
    const { enqueueSnackbar } = useSnackbar();
    const [myServiceOrderWarranty, setMyServiceOrderWarranty] = React.useState(serviceOrderWarranty)
    const [newWarrantiedServices, setNewWarrantedServices] = React.useState({})
    const [newWarrantiedParts, setNewWarrantedParts] = React.useState({})
    const [newWarrantiedCharges, setNewWarrantedCharges] = React.useState({})
    const [oldWarrantiedServices, setOldWarrantedServices] = React.useState({})
    const [oldWarrantiedParts, setOldWarrantedParts] = React.useState({})
    const [oldWarrantiedCharges, setOldWarrantedCharges] = React.useState({})
    const [saving, setSaving] = React.useState(false)
    React.useEffect(() => {
        setMyServiceOrderWarranty(serviceOrderWarranty)
        if (services && creatingNew) {
            const new_newWarrantiedServices = {};
            const new_newWarrantiedParts = {};
            const new_newWarrantiedCharges = {};
            services.forEach((service) => {
                new_newWarrantiedServices[service.id.toString()] = {
                    selected: false,
                    estimated_coverage: service.quoted_flat_rate ?? service.default_flat_rate,
                    actual_cost: service.quoted_flat_rate ?? service.default_flat_rate
                }
                service.service_order_parts.forEach((part) => {
                    new_newWarrantiedParts[part.id.toString()] = {
                        selected: false,
                        estimated_coverage: Math.round((part.quoted_cost ?? part.cost) * part.quantity * 100) / 100,
                        actual_cost: Math.round((part.quoted_cost ?? part.cost) * part.quantity * 100) / 100
                    }
                })
            });
            charges.forEach((charge) => {
                if (['SHOP_FEES', 'FEE', 'ADJUSTMENT'].includes(charge.charge_type)) {
                    new_newWarrantiedCharges[charge.id.toString()] = {
                        selected: false,
                        estimated_coverage: charge.estimated_cost,
                        actual_cost: charge.actual_cost
                    }
                }
            });
            setNewWarrantedServices({ ...new_newWarrantiedServices })
            setNewWarrantedParts({ ...new_newWarrantiedParts })
            setNewWarrantedCharges({ ...new_newWarrantiedCharges })
        } else if (!creatingNew) {
            const new_oldWarrantiedServices = {};
            const new_oldWarrantiedParts = {};
            const new_oldWarrantiedCharges = {};
            (serviceOrderWarranty?.service_order_services_association ?? []).forEach((service_assoc) => {
                new_oldWarrantiedServices[service_assoc.service_order_service.id.toString()] = {
                    name: service_assoc.service_order_service.name,
                    estimated_coverage: service_assoc.estimated_coverage,
                    actual_coverage: service_assoc.actual_coverage
                }
            });
            (serviceOrderWarranty?.service_order_parts_association ?? []).forEach((part_assoc) => {
                new_oldWarrantiedParts[part_assoc.service_order_part.id.toString()] = {
                    name: part_assoc.service_order_part.name,
                    estimated_coverage: part_assoc.estimated_coverage,
                    actual_coverage: part_assoc.actual_coverage,
                    sku: part_assoc.service_order_part.sku
                }
            });
            (serviceOrderWarranty?.service_order_charges_association ?? []).forEach((charge_assoc) => {
                const charge = charge_assoc.service_order_charge
                new_oldWarrantiedCharges[charge_assoc.service_order_charge.id.toString()] = {
                    name: charge.charge_type === 'SHOP_FEES' ? 'Shop Fees' : charge.charge_type === 'ADJUSTMENT' ? `Adjustment (${charge.description})` : charge.name,
                    estimated_coverage: charge_assoc.estimated_coverage,
                    actual_coverage: charge_assoc.actual_coverage,
                    sku: charge.sku
                }
            })
            setOldWarrantedServices({ ...new_oldWarrantiedServices })
            setOldWarrantedParts({ ...new_oldWarrantiedParts })
            setOldWarrantedCharges({ ...new_oldWarrantiedCharges })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    const handleChange = (prop) => (event) => {
        if (prop === "actual_discount") {
            const newValue = parseMoneyInput(myServiceOrderWarranty[prop], event.target.value, { allow_empty: true })
            setMyServiceOrderWarranty({ ...myServiceOrderWarranty, [prop]: newValue })
        } else if (["approved_for_discount"].includes(prop)) {
            setMyServiceOrderWarranty({ ...myServiceOrderWarranty, [prop]: event.target.checked })
        } else {
            setMyServiceOrderWarranty({ ...myServiceOrderWarranty, [prop]: event.target.value })
        }
    }

    const changeNew = (dict, dictSetter, id, prop) => (event) => {
        if (["estimated_coverage", "actual_coverage"].includes(prop)) {
            dict[id][prop] = parseMoneyInput(dict[id][prop], event.target.value, { allow_empty: true })
        } else if (prop === 'selected') {
            dict[id].selected = !dict[id].selected
        } else {
            dict[id][prop] = event.target.value
        }
        dictSetter({ ...dict })
    }

    const changeOld = (dict, dictSetter, id, prop) => (event) => {
        if (["estimated_coverage", "actual_coverage"].includes(prop)) {
            dict[id][prop] = parseMoneyInput(dict[id][prop], event.target.value, { allow_empty: true })
        } else {
            dict[id][prop] = event.target.value
        }
        dictSetter({ ...dict })
    }

    const save = (event) => {
        setSaving(true)
        if (creatingNew) {
            const body = {
                ...myServiceOrderWarranty,
                services: Object.keys(newWarrantiedServices).filter((x) => newWarrantiedServices[x].selected).map((x) => { return { id: x, estimated_coverage: newWarrantiedServices[x].estimated_coverage, actual_coverage: myServiceOrderWarranty.status === 'Fulfilled' ? newWarrantiedServices[x].actual_coverage : undefined } }),
                parts: Object.keys(newWarrantiedParts).filter((x) => newWarrantiedParts[x].selected).map((x) => { return { id: x, estimated_coverage: newWarrantiedParts[x].estimated_coverage, actual_coverage: myServiceOrderWarranty.status === 'Fulfilled' ? newWarrantiedParts[x].actual_coverage : undefined } }),
                charges: Object.keys(newWarrantiedCharges).filter((x) => newWarrantiedCharges[x].selected).map((x) => { return { id: x, estimated_coverage: newWarrantiedCharges[x].estimated_coverage, actual_coverage: myServiceOrderWarranty.status === 'Fulfilled' ? newWarrantiedCharges[x].actual_coverage : undefined } }),
            }
            if (body.services.length === 0 && body.parts.length === 0) {
                enqueueSnackbar('You must select at least 1 service or part on this warranty claim!', { variant: 'error' })
                setSaving(false)
                return
            }
            send_authorized_request(() =>
                AXIOS_API_INSTANCE.post(`serviceorders/${serviceOrderId}/create_warranty_claim`, body).then((serviceOrder) => {
                    setSaving(false)
                    enqueueSnackbar('Successfully created warranty claim', { variant: 'success' })
                    closeModal(serviceOrder.data.data)
                }).catch((error) => {
                    setSaving(false)
                    enqueueSnackbar(`Failed to create service order warranty claim <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
                })
            )
        } else {
            const body = {
                ...myServiceOrderWarranty,
                services: Object.keys(oldWarrantiedServices).map((x) => { return { id: x, estimated_coverage: oldWarrantiedServices[x].estimated_coverage, actual_coverage: myServiceOrderWarranty.status === 'Fulfilled' ? oldWarrantiedServices[x].actual_coverage : undefined } }),
                parts: Object.keys(oldWarrantiedParts).map((x) => { return { id: x, estimated_coverage: oldWarrantiedParts[x].estimated_coverage, actual_coverage: myServiceOrderWarranty.status === 'Fulfilled' ? oldWarrantiedParts[x].actual_coverage : undefined } }),
                charges: Object.keys(oldWarrantiedCharges).map((x) => { return { id: x, estimated_coverage: oldWarrantiedCharges[x].estimated_coverage, actual_coverage: myServiceOrderWarranty.status === 'Fulfilled' ? oldWarrantiedCharges[x].actual_coverage : undefined } }),
            }
            send_authorized_request(() =>
                AXIOS_API_INSTANCE.post(`serviceorderwarranties/${myServiceOrderWarranty.id}`, body
                ).then(() => {
                    enqueueSnackbar('Successfully updated warranty claim', { variant: 'success' })
                    setSaving(false)
                    closeModal(true)
                }).catch((error) => {
                    setSaving(false)
                    enqueueSnackbar(`Failed to update warranty claim <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
                })
            )
        }
    }
    const del = () => {
        setSaving(true)
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.delete(`serviceorderwarranties/${myServiceOrderWarranty.id}`).then(() => {
                setSaving(false)
                enqueueSnackbar('Successfully deleted warranty claim', { variant: 'success' })
                closeModal(true)
            }).catch((error) => {
                setSaving(false)
                enqueueSnackbar(`Failed to delete warranty claim <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
            })
        )
    }

    return (
        (<div>
            <Dialog
                open={open}
                onClose={() => closeModal(false)}
                maxWidth={'lg'}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <LoadingBackdropIndeterminate
                    open={saving}
                    text={"Saving Warranty Claim"}
                />
                <DialogTitle>
                    <span style={{ "paddingRight": "24px" }}>{creatingNew ? "Creating Warranty Claim" : "Modifying Warranty Claim"}</span>
                    <IconButton
                        aria-label="close"
                        onClick={() => closeModal(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box>
                        <Grid container direction="column" spacing={1}>
                            <Grid size="grow">
                                <TextField
                                    margin="dense"
                                    id="vendor"
                                    label="Vendor"
                                    type="text"
                                    fullWidth
                                    variant="filled"
                                    value={myServiceOrderWarranty?.vendor ?? ''}
                                    onChange={handleChange("vendor")}
                                />
                            </Grid>
                            <Grid size="grow">
                                <TextField
                                    margin="dense"
                                    id="claim_number"
                                    label="Claim Number"
                                    type="text"
                                    fullWidth
                                    variant="filled"
                                    value={myServiceOrderWarranty?.claim_number ?? ''}
                                    onChange={handleChange("claim_number")}
                                />
                            </Grid>
                            <Grid size="grow">
                                <Box width={"100%"} sx={{ pt: 1 }}>
                                    <FormControl fullWidth required>
                                        <InputLabel id="service-order-warranty-status">Status</InputLabel>
                                        <Select
                                            labelId="service-order-warranty-status"
                                            label="Status"
                                            placeholder='Select Status...'
                                            required
                                            onChange={handleChange("status")}
                                            value={myServiceOrderWarranty.status ?? ''}
                                            sx={{ width: '100%' }}
                                        >
                                            {statuses.map((status) =>
                                                <MenuItem
                                                    key={status}
                                                    value={status}
                                                >
                                                    {status}
                                                </MenuItem>
                                            )}
                                        </Select>
                                    </FormControl>
                                </Box>
                            </Grid>
                            <Grid size="grow">
                                <span><b>Customer Applied Discount: ${moneyFormat(findTotalDiscount(newWarrantiedServices, newWarrantiedParts, newWarrantiedCharges, oldWarrantiedServices, oldWarrantiedParts, oldWarrantiedCharges))}</b></span>
                            </Grid>
                            <Grid size="grow">
                                <Tooltip
                                    title="Apply Customer Discount to Service Order"
                                    arrow
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={myServiceOrderWarranty?.approved_for_discount || false}
                                                onChange={handleChange("approved_for_discount")}
                                            />
                                        }
                                        label="Approved For Discount"
                                    />
                                </Tooltip>
                            </Grid>
                        </Grid>
                        {creatingNew && <Box>
                            <h3>Claimed Services/Parts</h3>
                            {services.map((service) => {
                                return (
                                    (<Accordion key={service.id}>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            aria-controls="customeraccordion-content"
                                            id="customeraccordion-header"
                                        >
                                            <Typography>{service.name}</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Box>
                                                <Grid container alignItems={'center'} spacing={1}>
                                                    <Grid size="grow">
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={newWarrantiedServices[service.id.toString()]?.selected || false}
                                                                    onChange={changeNew(newWarrantiedServices, setNewWarrantedServices, service.id.toString(), 'selected')}
                                                                />
                                                            }
                                                            label="Claim Service"
                                                        />
                                                    </Grid>
                                                    <Grid size="grow">
                                                        <TextField
                                                            margin="dense"
                                                            id="estimated_coverage"
                                                            label="Amount Covered"
                                                            type="text"
                                                            fullWidth
                                                            variant="filled"
                                                            value={newWarrantiedServices[service.id.toString()]?.estimated_coverage}
                                                            onChange={changeNew(newWarrantiedServices, setNewWarrantedServices, service.id.toString(), 'estimated_coverage')}
                                                            // error={newWarrantiedServices[service.id.toString()]?.estimated_coverage !== newWarrantiedServices[service.id.toString()]?.actual_cost}
                                                            helperText={newWarrantiedServices[service.id.toString()]?.estimated_coverage !== newWarrantiedServices[service.id.toString()]?.actual_cost ? `Warranty Coverage and customer cost ($${moneyFormat(newWarrantiedServices[service.id.toString()]?.actual_cost)}) are not the same` : ''}
                                                            slotProps={{
                                                                input: {
                                                                    startAdornment: (
                                                                        <InputAdornment position="start">
                                                                            {<AttachMoneyIcon />}
                                                                        </InputAdornment>
                                                                    ),
                                                                },

                                                                htmlInput: { inputMode: 'decimal' }
                                                            }} />
                                                    </Grid>
                                                    {myServiceOrderWarranty.status === 'Fulfilled' &&
                                                        <Grid size="grow">
                                                            <TextField
                                                                margin="dense"
                                                                id="actual_coverage"
                                                                label="Vendor Amount Covered"
                                                                type="text"
                                                                fullWidth
                                                                variant="filled"
                                                                value={newWarrantiedServices[service.id.toString()]?.actual_coverage ?? ''}
                                                                onChange={changeNew(newWarrantiedServices, setNewWarrantedServices, service.id.toString(), 'actual_coverage')}
                                                                error={newWarrantiedServices[service.id.toString()].actual_coverage === undefined || newWarrantiedServices[service.id.toString()].actual_coverage === null}
                                                                slotProps={{
                                                                    input: {
                                                                        startAdornment: (
                                                                            <InputAdornment position="start">
                                                                                {<AttachMoneyIcon />}
                                                                            </InputAdornment>
                                                                        ),
                                                                    },

                                                                    htmlInput: { inputMode: 'decimal' }
                                                                }} />
                                                        </Grid>
                                                    }
                                                </Grid>
                                                {service.service_order_parts.length > 0 &&
                                                    <Box>
                                                        <h4>Parts</h4>
                                                        {service.service_order_parts.sort((a, b) => dayjs.utc(a.date_created).unix() - dayjs.utc(b.date_created).unix()).map((part) => {
                                                            return (
                                                                (<Grid container alignItems={'center'} spacing={1} key={part.id}>
                                                                    <Grid size="grow">
                                                                        <FormControlLabel
                                                                            control={
                                                                                <Checkbox
                                                                                    checked={newWarrantiedParts[part.id.toString()]?.selected || false}
                                                                                    onChange={changeNew(newWarrantiedParts, setNewWarrantedParts, part.id.toString(), 'selected')}
                                                                                />
                                                                            }
                                                                            label={`Claim ${part.name}`}
                                                                        />
                                                                    </Grid>
                                                                    <Grid size="grow">
                                                                        <TextField
                                                                            margin="dense"
                                                                            id="estimated_coverage"
                                                                            label="Amount Covered"
                                                                            type="text"
                                                                            fullWidth
                                                                            variant="filled"
                                                                            value={newWarrantiedParts[part.id.toString()]?.estimated_coverage}
                                                                            onChange={changeNew(newWarrantiedParts, setNewWarrantedParts, part.id.toString(), 'estimated_coverage')}
                                                                            // error={newWarrantiedParts[part.id.toString()]?.estimated_coverage !== newWarrantiedParts[part.id.toString()]?.actual_cost}
                                                                            helperText={newWarrantiedParts[part.id.toString()]?.estimated_coverage !== (newWarrantiedParts[part.id.toString()]?.actual_cost * newWarrantiedParts[part.id.toString()]?.quantity) ? `Warranty Coverage and customer cost ($${moneyFormat(newWarrantiedParts[part.id.toString()]?.actual_cost * newWarrantiedParts[part.id.toString()]?.quantity)}) are not the same` : ''}
                                                                            slotProps={{
                                                                                input: {
                                                                                    startAdornment: (
                                                                                        <InputAdornment position="start">
                                                                                            {<AttachMoneyIcon />}
                                                                                        </InputAdornment>
                                                                                    ),
                                                                                },

                                                                                htmlInput: { inputMode: 'decimal' }
                                                                            }} />
                                                                    </Grid>
                                                                    {myServiceOrderWarranty.status === 'Fulfilled' &&
                                                                        <Grid size="grow">
                                                                            <TextField
                                                                                margin="dense"
                                                                                id="actual_coverage"
                                                                                label="Vendor Amount Covered"
                                                                                type="text"
                                                                                fullWidth
                                                                                variant="filled"
                                                                                value={newWarrantiedParts[part.id.toString()]?.actual_coverage ?? ''}
                                                                                onChange={changeNew(newWarrantiedParts, setNewWarrantedParts, part.id.toString(), 'actual_coverage')}
                                                                                error={newWarrantiedParts[part.id.toString()].actual_coverage === undefined || newWarrantiedParts[part.id.toString()].actual_coverage === null}
                                                                                slotProps={{
                                                                                    input: {
                                                                                        startAdornment: (
                                                                                            <InputAdornment position="start">
                                                                                                {<AttachMoneyIcon />}
                                                                                            </InputAdornment>
                                                                                        ),
                                                                                    },

                                                                                    htmlInput: { inputMode: 'decimal' }
                                                                                }} />
                                                                        </Grid>
                                                                    }
                                                                </Grid>)
                                                            );
                                                        })}
                                                    </Box>
                                                }
                                            </Box>
                                        </AccordionDetails>
                                    </Accordion>)
                                );
                            })}
                            {charges.filter((x) => ['SHOP_FEES', 'FEE', 'ADJUSTMENT'].includes(x.charge_type)).length > 0 &&
                                <Box>
                                    <h3>Claimed Extras</h3>
                                    <Accordion>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            aria-controls="customeraccordion-content"
                                            id="customeraccordion-header"
                                        >
                                            <Typography>Extras</Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            {charges.filter((x) => ['SHOP_FEES', 'FEE', 'ADJUSTMENT'].includes(x.charge_type)).sort((a, b) => {
                                                if (a.charge_type !== b.charge_type) {
                                                    return ['SHOP_FEES', 'FEE', 'ADJUSTMENT'].indexOf(a.charge_type) - ['SHOP_FEES', 'FEE', 'ADJUSTMENT'].indexOf(b.charge_type)
                                                } else {
                                                    return dayjs.utc(a.date_created).unix() - dayjs.utc(b.date_created).unix()
                                                }
                                            }).map((charge) =>
                                                <Grid key={charge.id} container alignItems={'center'} spacing={1}>
                                                    <Grid size="grow">
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={newWarrantiedCharges[charge.id.toString()]?.selected || false}
                                                                    onChange={changeNew(newWarrantiedCharges, setNewWarrantedCharges, charge.id.toString(), 'selected')}
                                                                />
                                                            }
                                                            label={`Claim ${charge.charge_type === 'SHOP_FEES' ? 'Shop Fees' : charge.charge_type === 'ADJUSTMENT' ? `Adjustment (${charge.description})` : charge.name}`}
                                                        />
                                                    </Grid>
                                                    <Grid size="grow">
                                                        <TextField
                                                            margin="dense"
                                                            id="estimated_coverage"
                                                            label="Amount Covered"
                                                            type="text"
                                                            fullWidth
                                                            variant="filled"
                                                            value={newWarrantiedCharges[charge.id.toString()]?.estimated_coverage}
                                                            onChange={changeNew(newWarrantiedCharges, setNewWarrantedCharges, charge.id.toString(), 'estimated_coverage')}
                                                            // error={newWarrantiedCharges[charge.id.toString()]?.estimated_coverage !== newWarrantiedCharges[charge.id.toString()]?.actual_cost}
                                                            helperText={newWarrantiedCharges[charge.id.toString()]?.estimated_coverage !== newWarrantiedCharges[charge.id.toString()]?.actual_cost ? `Warranty Coverage and customer cost ($${moneyFormat(newWarrantiedCharges[charge.id.toString()]?.actual_cost)}) are not the same` : ''}
                                                            slotProps={{
                                                                input: {
                                                                    startAdornment: (
                                                                        <InputAdornment position="start">
                                                                            {<AttachMoneyIcon />}
                                                                        </InputAdornment>
                                                                    ),
                                                                },

                                                                htmlInput: { inputMode: 'decimal' }
                                                            }} />
                                                    </Grid>
                                                    {myServiceOrderWarranty.status === 'Fulfilled' &&
                                                        <Grid size="grow">
                                                            <TextField
                                                                margin="dense"
                                                                id="actual_coverage"
                                                                label="Vendor Amount Covered"
                                                                type="text"
                                                                fullWidth
                                                                variant="filled"
                                                                value={newWarrantiedCharges[charge.id.toString()]?.actual_coverage ?? ''}
                                                                onChange={changeNew(newWarrantiedCharges, setNewWarrantedCharges, charge.id.toString(), 'actual_coverage')}
                                                                error={newWarrantiedCharges[charge.id.toString()].actual_coverage === undefined || newWarrantiedCharges[charge.id.toString()].actual_coverage === null}
                                                                slotProps={{
                                                                    input: {
                                                                        startAdornment: (
                                                                            <InputAdornment position="start">
                                                                                {<AttachMoneyIcon />}
                                                                            </InputAdornment>
                                                                        ),
                                                                    },

                                                                    htmlInput: { inputMode: 'decimal' }
                                                                }} />
                                                        </Grid>
                                                    }
                                                </Grid>
                                            )}
                                        </AccordionDetails>
                                    </Accordion>
                                </Box>
                            }
                        </Box>}
                        {!creatingNew && <Box>
                            {Object.keys(oldWarrantiedServices).length > 0 &&
                                <Box>
                                    <h3>Services</h3>
                                    {Object.keys(oldWarrantiedServices).map((service_id) => {
                                        return (
                                            (<Grid container alignItems={'center'} spacing={1} key={service_id}>
                                                <Grid size="grow">
                                                    <span>{oldWarrantiedServices[service_id].name}</span>
                                                </Grid>
                                                <Grid size="grow">
                                                    <TextField
                                                        margin="dense"
                                                        id="estimated_coverage"
                                                        label="Amount Covered"
                                                        type="text"
                                                        fullWidth
                                                        variant="filled"
                                                        value={oldWarrantiedServices[service_id].estimated_coverage}
                                                        onChange={changeOld(oldWarrantiedServices, setOldWarrantedServices, service_id, 'estimated_coverage')}
                                                        slotProps={{
                                                            input: {
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        {<AttachMoneyIcon />}
                                                                    </InputAdornment>
                                                                ),
                                                            },

                                                            htmlInput: { inputMode: 'decimal' }
                                                        }} />
                                                </Grid>
                                                {myServiceOrderWarranty.status === 'Fulfilled' &&
                                                    <Grid size="grow">
                                                        <TextField
                                                            margin="dense"
                                                            id="actual_coverage"
                                                            label="Vendor Amount Covered"
                                                            type="text"
                                                            fullWidth
                                                            variant="filled"
                                                            value={oldWarrantiedServices[service_id].actual_coverage ?? ''}
                                                            onChange={changeOld(oldWarrantiedServices, setOldWarrantedServices, service_id, 'actual_coverage')}
                                                            error={oldWarrantiedServices[service_id].actual_coverage === undefined || oldWarrantiedServices[service_id].actual_coverage === null}
                                                            slotProps={{
                                                                input: {
                                                                    startAdornment: (
                                                                        <InputAdornment position="start">
                                                                            {<AttachMoneyIcon />}
                                                                        </InputAdornment>
                                                                    ),
                                                                },

                                                                htmlInput: { inputMode: 'decimal' }
                                                            }} />
                                                    </Grid>
                                                }
                                            </Grid>)
                                        );
                                    })
                                    }
                                </Box>
                            }
                            {Object.keys(oldWarrantiedParts).length > 0 &&
                                <Box>
                                    <h3>Parts</h3>
                                    {Object.keys(oldWarrantiedParts).map((part_id) => {
                                        return (
                                            (<Grid container alignItems={'center'} spacing={1} key={part_id}>
                                                <Grid container direction={'column'} size="grow">
                                                    <Grid size="grow">
                                                        <span>{oldWarrantiedParts[part_id].name} <IconButton size="small" onClick={() => navigator.clipboard.writeText(oldWarrantiedParts[part_id].name)}>
                                                            <ContentCopyIcon />
                                                        </IconButton></span>
                                                    </Grid>
                                                    <Grid size="grow">
                                                        <span><b>SKU:</b> {oldWarrantiedParts[part_id].sku} <IconButton size="small" onClick={() => navigator.clipboard.writeText(oldWarrantiedParts[part_id].sku)}>
                                                            <ContentCopyIcon />
                                                        </IconButton></span>
                                                    </Grid>
                                                </Grid>
                                                <Grid size="grow">
                                                    <TextField
                                                        margin="dense"
                                                        id="estimated_coverage"
                                                        label="Amount Covered"
                                                        type="text"
                                                        fullWidth
                                                        variant="filled"
                                                        value={oldWarrantiedParts[part_id].estimated_coverage}
                                                        onChange={changeOld(oldWarrantiedParts, setOldWarrantedParts, part_id, 'estimated_coverage')}
                                                        slotProps={{
                                                            input: {
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        {<AttachMoneyIcon />}
                                                                    </InputAdornment>
                                                                ),
                                                            },

                                                            htmlInput: { inputMode: 'decimal' }
                                                        }} />
                                                </Grid>
                                                {myServiceOrderWarranty.status === 'Fulfilled' &&
                                                    <Grid size="grow">
                                                        <TextField
                                                            margin="dense"
                                                            id="actual_coverage"
                                                            label="Vendor Amount Covered"
                                                            type="text"
                                                            fullWidth
                                                            variant="filled"
                                                            value={oldWarrantiedParts[part_id].actual_coverage ?? ''}
                                                            onChange={changeOld(oldWarrantiedParts, setOldWarrantedParts, part_id, 'actual_coverage')}
                                                            error={oldWarrantiedParts[part_id].actual_coverage === undefined || oldWarrantiedParts[part_id].actual_coverage === null}
                                                            slotProps={{
                                                                input: {
                                                                    startAdornment: (
                                                                        <InputAdornment position="start">
                                                                            {<AttachMoneyIcon />}
                                                                        </InputAdornment>
                                                                    ),
                                                                },

                                                                htmlInput: { inputMode: 'decimal' }
                                                            }} />
                                                    </Grid>
                                                }
                                            </Grid>)
                                        );
                                    })
                                    }
                                </Box>
                            }
                            {Object.keys(oldWarrantiedCharges).length > 0 &&
                                <Box>
                                    <h3>Extras</h3>
                                    {Object.keys(oldWarrantiedCharges).map((charge_id) => {
                                        return (
                                            (<Grid container alignItems={'center'} spacing={1} key={charge_id}>
                                                <Grid container direction={'column'} size="grow" justifyContent={'space-around'} rowSpacing={0}>
                                                    <Grid size="grow">
                                                        <span>{oldWarrantiedCharges[charge_id].name}</span>
                                                    </Grid>
                                                    <Grid size="grow">
                                                        <span><b>SKU:</b> {oldWarrantiedCharges[charge_id].sku} <IconButton size="small" onClick={() => navigator.clipboard.writeText(oldWarrantiedCharges[charge_id].sku)}>
                                                            <ContentCopyIcon />
                                                        </IconButton></span>
                                                    </Grid>
                                                </Grid>
                                                <Grid size="grow">
                                                    <TextField
                                                        margin="dense"
                                                        id="estimated_coverage"
                                                        label="Amount Covered"
                                                        type="text"
                                                        fullWidth
                                                        variant="filled"
                                                        value={oldWarrantiedCharges[charge_id].estimated_coverage}
                                                        onChange={changeOld(oldWarrantiedCharges, setOldWarrantedCharges, charge_id, 'estimated_coverage')}
                                                        slotProps={{
                                                            input: {
                                                                startAdornment: (
                                                                    <InputAdornment position="start">
                                                                        {<AttachMoneyIcon />}
                                                                    </InputAdornment>
                                                                ),
                                                            },

                                                            htmlInput: { inputMode: 'decimal' }
                                                        }} />
                                                </Grid>
                                                {myServiceOrderWarranty.status === 'Fulfilled' &&
                                                    <Grid size="grow">
                                                        <TextField
                                                            margin="dense"
                                                            id="actual_coverage"
                                                            label="Vendor Amount Covered"
                                                            type="text"
                                                            fullWidth
                                                            variant="filled"
                                                            value={oldWarrantiedCharges[charge_id].actual_coverage ?? ''}
                                                            onChange={changeOld(oldWarrantiedCharges, setOldWarrantedCharges, charge_id, 'actual_coverage')}
                                                            error={oldWarrantiedCharges[charge_id].actual_coverage === undefined || oldWarrantiedCharges[charge_id].actual_coverage === null}
                                                            slotProps={{
                                                                input: {
                                                                    startAdornment: (
                                                                        <InputAdornment position="start">
                                                                            {<AttachMoneyIcon />}
                                                                        </InputAdornment>
                                                                    ),
                                                                },

                                                                htmlInput: { inputMode: 'decimal' }
                                                            }} />
                                                    </Grid>
                                                }
                                                <Grid size={12}>
                                                    <Divider />
                                                </Grid>
                                            </Grid>)
                                        );
                                    })
                                    }
                                </Box>
                            }
                        </Box>}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Grid container justifyContent="space-between" sx={{ padding: "0px 30px 10px 30px", width: '100%' }}>
                        <Grid size="auto">
                            {!creatingNew &&
                                <Tooltip title={myServiceOrderWarranty?.deletable_reason}>
                                    <span> {/* span required for tooltip events */}
                                        <Button disabled={!myServiceOrderWarranty?.deletable} variant='outlined' color='error' onClick={del} sx={{ mr: 1 }}>
                                            <DeleteIcon /> <Box sx={{ pl: 1 }}><span>Delete</span></Box>
                                        </Button>
                                    </span>
                                </Tooltip>
                            }
                        </Grid>
                        <Grid size="auto">
                            <Button variant="contained" autoFocus onClick={save}>
                                {creatingNew ? "Create" : "Save"}
                            </Button>
                        </Grid>
                    </Grid>
                </DialogActions>
            </Dialog>
        </div>)
    );
}
